import React, {lazy, Suspense, useId} from "react";
import {Alert, Button, Modal, ModalBody, ModalHeader, Spinner} from "reactstrap";
import {useOfficeTranslation} from "i18n/OfficeTranslation";

const PdfDocumentViewer = lazy(() => import("components/PdfDocumentViewer"));

export interface PdfPreview {
    name: string;
    source: string;
}

interface Props {
    preview: PdfPreview | null;
    onClose: () => void;
}

// Keep the PDF and its object URL in the application's browsing context.
const PdfPreviewModal = ({preview, onClose}: Props) => {
    const {t} = useOfficeTranslation();
    const titleId = useId();
    const loading = <div role="status" className="text-center py-5">
        <Spinner size="sm" aria-hidden="true" /> {t("email.attachment.loading")}
    </div>;

    return <Modal isOpen={preview !== null} toggle={onClose} size="xl" fullscreen="md"
        scrollable labelledBy={titleId}>
        <ModalHeader className="email-attachment-viewer-header" close={
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {preview && <Button tag="a" color="light" href={preview.source} download={preview.name}
                    className="email-attachment-viewer-download" aria-label={t("email.attachment.download")}
                    title={t("email.attachment.download")}>
                    <i className="mdi mdi-download" aria-hidden="true" />
                </Button>}
                <button type="button" className="btn-close m-0" aria-label={t("common.close")}
                    onClick={onClose} />
            </div>
        }>
            <span id={titleId} className="text-break">{preview?.name}</span>
        </ModalHeader>
        <ModalBody>
            {preview && <Suspense fallback={loading}>
                <PdfDocumentViewer key={preview.source} url={preview.source} className=""
                    loading={loading}
                    error={<Alert color="danger" fade={false}>{t("email.attachment.loadFailed")}</Alert>} />
            </Suspense>}
        </ModalBody>
    </Modal>;
};

export default PdfPreviewModal;
